import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Star, Clock, ExternalLink, Utensils, Coffee, Landmark, TreePine } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const CATEGORY_KEYS = [
  { id: "alle", key: "reco_cat_all", icon: Star },
  { id: "restaurant", key: "reco_cat_restaurant", icon: Utensils },
  { id: "cafe", key: "reco_cat_cafe", icon: Coffee },
  { id: "sehenswuerdigkeit", key: "reco_cat_sight", icon: Landmark },
  { id: "natur", key: "reco_cat_nature", icon: TreePine },
];

const RECOMMENDATIONS = [
  {
    id: 1,
    name: "Figlmüller",
    category: "restaurant",
    tagline: "Das Original Wiener Schnitzel",
    description: "Seit 1905 serviert Figlmüller das berühmteste Schnitzel Wiens – tellergroß, butterweich und goldbraun paniert. Ein absolutes Muss für jeden Wien-Besucher.",
    address: "Wollzeile 5, 1010 Wien",
    hours: "11:00 – 22:00",
    rating: "4.8",
    image: "https://images.unsplash.com/photo-1599921841143-819065a55cc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxXaWVuZXIlMjBTY2huaXR6ZWwlMjByZXN0YXVyYW50JTIwZm9vZHxlbnwwfHx8fDE3NzcxOTkwMDd8MA&ixlib=rb-4.1.0&q=85",
    link: "https://www.figlmueller.at",
    tip: "Unbedingt reservieren – besonders am Wochenende!"
  },
  {
    id: 2,
    name: "Café Central",
    category: "cafe",
    tagline: "Wiener Kaffeehauskultur vom Feinsten",
    description: "Das legendäre Café Central ist seit 1876 ein Treffpunkt für Intellektuelle und Genießer. Genießen Sie eine Wiener Melange unter den prachtvollen Gewölbedecken.",
    address: "Herrengasse 14, 1010 Wien",
    hours: "08:00 – 22:00",
    rating: "4.6",
    image: "https://images.unsplash.com/photo-1765045038226-43d7cd5cfd29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxWaWVubmElMjBjYWZlJTIwdHJhZGl0aW9uYWx8ZW58MHx8fHwxNzc3MTk5MDA3fDA&ixlib=rb-4.1.0&q=85",
    link: "https://www.cafecentral.wien",
    tip: "Probieren Sie unbedingt die Apfelstrudel-Vorführung!"
  },
  {
    id: 3,
    name: "Schloss Schönbrunn",
    category: "sehenswuerdigkeit",
    tagline: "Die kaiserliche Sommerresidenz",
    description: "Das UNESCO-Weltkulturerbe mit 1.441 Zimmern, dem beeindruckenden Schlosspark und dem ältesten Zoo der Welt. Erleben Sie die Geschichte der Habsburger hautnah.",
    address: "Schönbrunner Schloßstraße 47, 1130 Wien",
    hours: "09:00 – 17:00",
    rating: "4.7",
    image: "https://images.pexels.com/photos/29689459/pexels-photo-29689459.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    link: "https://www.schoenbrunn.at",
    tip: "Der Kronprinzengarten und die Gloriette bieten den besten Ausblick!"
  },
  {
    id: 4,
    name: "Naschmarkt",
    category: "restaurant",
    tagline: "Wiens beliebtester Markt seit 1774",
    description: "Über 120 Marktstände und Restaurants bieten kulinarische Köstlichkeiten aus aller Welt. Vom orientalischen Gewürzstand bis zum frischen Wiener Käse – hier findet jeder etwas.",
    address: "Naschmarkt, 1060 Wien",
    hours: "06:00 – 19:30 (Mo-Sa)",
    rating: "4.5",
    image: "https://images.pexels.com/photos/14093977/pexels-photo-14093977.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    link: "https://www.wien.info/de/essen-trinken/maerkte/naschmarkt",
    tip: "Samstags mit dem Flohmarkt kombinieren – Schnäppchenjäger aufgepasst!"
  },
  {
    id: 5,
    name: "Stephansdom",
    category: "sehenswuerdigkeit",
    tagline: "Das Wahrzeichen Wiens",
    description: "Der gotische Stephansdom thront seit über 800 Jahren im Herzen der Stadt. Besteigen Sie den Südturm (343 Stufen) für einen atemberaubenden Panoramablick über Wien.",
    address: "Stephansplatz 3, 1010 Wien",
    hours: "06:00 – 22:00",
    rating: "4.8",
    image: "https://images.pexels.com/photos/34845732/pexels-photo-34845732.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    link: "https://www.stephanskirche.at",
    tip: "Die Katakomben-Führung ist faszinierend – nichts für schwache Nerven!"
  },
  {
    id: 6,
    name: "Belvedere",
    category: "sehenswuerdigkeit",
    tagline: "Kunst & Barock-Architektur",
    description: "Das Schloss Belvedere beherbergt Klimts berühmten 'Kuss' und bietet mit seiner barocken Gartenanlage einen der schönsten Blicke auf die Wiener Skyline.",
    address: "Prinz Eugen-Straße 27, 1030 Wien",
    hours: "10:00 – 18:00",
    rating: "4.7",
    image: "https://images.pexels.com/photos/32639169/pexels-photo-32639169.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    link: "https://www.belvedere.at",
    tip: "Das Untere Belvedere hat oft weniger Besucher – perfekt für Kunstgenuss in Ruhe."
  },
  {
    id: 7,
    name: "Wiener Prater",
    category: "natur",
    tagline: "Riesenrad & Grüne Oase",
    description: "Der Wiener Prater vereint den legendären Vergnügungspark mit dem Riesenrad und die weitläufige Prater Hauptallee – perfekt zum Spazieren, Radfahren oder Picknicken.",
    address: "Prater, 1020 Wien",
    hours: "Ganztägig (Park) / 10:00-23:45 (Riesenrad)",
    rating: "4.6",
    image: "https://images.pexels.com/photos/15266872/pexels-photo-15266872.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    link: "https://www.praterwien.com",
    tip: "Eine Fahrt mit dem Riesenrad bei Sonnenuntergang ist unvergesslich!"
  },
  {
    id: 8,
    name: "Café Hawelka",
    category: "cafe",
    tagline: "Bohème-Flair seit 1939",
    description: "Das authentischste Wiener Kaffeehaus abseits der Touristenpfade. Berühmt für die Buchteln, die ab 22 Uhr frisch aus dem Ofen kommen. Hier trifft Tradition auf Charme.",
    address: "Dorotheergasse 6, 1010 Wien",
    hours: "08:00 – 01:00",
    rating: "4.4",
    image: "https://images.unsplash.com/photo-1614447927538-c4c8df03c916?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxWaWVubmElMjBjYWZlJTIwdHJhZGl0aW9uYWx8ZW58MHx8fHwxNzc3MTk5MDA3fDA&ixlib=rb-4.1.0&q=85",
    link: "https://www.hawelka.at",
    tip: "Kommen Sie abends für die berühmten Buchteln – sie sind schnell vergriffen!"
  },
  {
    id: 9,
    name: "Stadtpark",
    category: "natur",
    tagline: "Wiens grüne Seele",
    description: "Der wunderschöne Stadtpark mit dem goldenen Johann-Strauss-Denkmal ist die perfekte Pause vom Sightseeing. Genießen Sie die Ruhe entlang des Wienflusses.",
    address: "Parkring 1, 1010 Wien",
    hours: "Ganztägig",
    rating: "4.5",
    image: "https://images.unsplash.com/photo-1663062242357-1fe8ee06566c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxCZWx2ZWRlcmUlMjBWaWVubmElMjBwYWxhY2V8ZW58MHx8fHwxNzc3MTk5MDA3fDA&ixlib=rb-4.1.0&q=85",
    link: "https://www.wien.info/de/sightseeing/stadtpark",
    tip: "Perfekt für ein Morgenjoggen oder einen romantischen Abendspaziergang."
  },
];

function useInView(ref, threshold = 0.15) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return isVisible;
}

function RecommendationCard({ item, index }) {
  const ref = useRef(null);
  const isVisible = useInView(ref);
  const [imgLoaded, setImgLoaded] = useState(false);
  const { t } = useLanguage();
  const catInfo = CATEGORY_KEYS.find((c) => c.id === item.category);

  return (
    <div
      ref={ref}
      data-testid={`recommendation-card-${item.id}`}
      className="group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(40px)",
        transition: `all 0.7s cubic-bezier(0.25, 1, 0.5, 1) ${index * 100}ms`,
      }}
    >
      <div className="bg-white rounded-3xl border border-black/5 overflow-hidden transition-all duration-500 hover:shadow-[0_20px_60px_rgba(0,0,0,0.1)] hover:-translate-y-1">
        {/* Image */}
        <div className="relative aspect-[16/10] overflow-hidden">
          {!imgLoaded && <div className="absolute inset-0 img-skeleton" />}
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          />
          {/* Category Badge */}
          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center gap-1.5 crystal-glass-strong rounded-full px-3 py-1.5 text-xs font-semibold text-[#1D1D1F]">
              {catInfo && <catInfo.icon className="w-3 h-3" />}
              {catInfo ? t(catInfo.key) : ""}
            </span>
          </div>
          {/* Rating */}
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1 bg-black/70 backdrop-blur-md text-white rounded-full px-3 py-1.5 text-xs font-semibold">
              <Star className="w-3 h-3 fill-current" />
              {item.rating}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <h3
            className="text-xl font-semibold tracking-tight text-[#1D1D1F] mb-1"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {item.name}
          </h3>
          <p className="text-sm font-medium text-[#86868B] mb-3">{item.tagline}</p>
          <p className="text-sm text-[#1D1D1F] leading-relaxed mb-4 line-clamp-3">
            {item.description}
          </p>

          {/* Meta */}
          <div className="flex flex-col gap-2 mb-4">
            <div className="flex items-center gap-2 text-xs text-[#86868B]">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span>{item.address}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#86868B]">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span>{item.hours}</span>
            </div>
          </div>

          {/* Tip */}
          {item.tip && (
            <div className="bg-[#F5F5F7] rounded-xl px-4 py-3 mb-4">
              <p className="text-xs text-[#1D1D1F]">
                <span className="font-semibold">{t("reco_tip")}:</span> {item.tip}
              </p>
            </div>
          )}

          {/* Link */}
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`recommendation-link-${item.id}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#1D1D1F] hover:text-[#86868B] transition-colors group/link"
          >
            {t("reco_website")}
            <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-0.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default function RecommendationsPage() {
  const [activeCategory, setActiveCategory] = useState("alle");
  const heroRef = useRef(null);
  const heroVisible = useInView(heroRef, 0.1);
  const { t } = useLanguage();

  const filtered =
    activeCategory === "alle"
      ? RECOMMENDATIONS
      : RECOMMENDATIONS.filter((r) => r.category === activeCategory);

  return (
    <div data-testid="recommendations-page" className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>{`${t("reco_title")} | Oona Flats`}</title>
        <meta name="description" content={t("reco_subtitle")} />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section ref={heroRef} className="pt-28 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto text-center" style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? "translateY(0)" : "translateY(30px)", transition: "all 0.8s cubic-bezier(0.25, 1, 0.5, 1)" }}>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#86868B] mb-4">{t("reco_label")}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tighter text-[#1D1D1F] mb-6 max-w-4xl mx-auto leading-[0.95]" style={{ fontFamily: "Outfit, sans-serif" }}>{t("reco_title")}</h1>
          <p className="text-base md:text-lg text-[#86868B] max-w-2xl mx-auto leading-relaxed" style={{ opacity: heroVisible ? 1 : 0, transform: heroVisible ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s cubic-bezier(0.25, 1, 0.5, 1) 200ms" }}>
            {t("reco_subtitle")}
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-20 z-40 px-6 md:px-12 lg:px-24 pb-8">
        <div className="max-w-7xl mx-auto flex justify-center">
          <div data-testid="category-tabs" className="inline-flex items-center gap-1 crystal-glass-strong rounded-full p-1.5 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
            {CATEGORY_KEYS.map((cat) => (
              <button key={cat.id} data-testid={`category-tab-${cat.id}`} onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeCategory === cat.id ? "bg-black text-white shadow-md" : "text-[#86868B] hover:text-[#1D1D1F] hover:bg-black/5"}`}>
                <cat.icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t(cat.key)}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Recommendations Grid */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((item, i) => (<RecommendationCard key={item.id} item={item} index={i} />))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-20"><p className="text-xl text-[#86868B]">{t("reco_none")}</p></div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
